import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
} from "../../lib/emsApproval";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createTrainingCompetenceRegisterItemApi,
  decideEmsApprovalApi,
  deleteTrainingCompetenceRegisterItemApi,
  downloadTrainingCompetenceRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchTrainingCompetenceRegisterApi,
  requestEmsApprovalApi,
  updateTrainingCompetenceRegisterItemApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "training_competence";
const COMPLETION_STATUSES = ["completed"];
const STATUS_OPTIONS = ["planned", "booked", "completed", "expired", "overdue"];

const EMPTY_FORM = {
  iso_clause: "",
  person_name: "",
  role_title: "",
  training_topic: "",
  competence_area: "",
  provider: "",
  due_date: "",
  completed_date: "",
  owner: "",
  status: "planned",
  evidence: "",
  notes: "",
};

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function TrainingCompetence() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [defaultIsoClause, setDefaultIsoClause] = useState("7");
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [form, setForm] = useState({ ...EMPTY_FORM, iso_clause: "7" });

  const role = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(role);
  const canApprove = approveRoles.includes(role) && canApproveEmsModule("training_competence");
  const canReview = reviewRoles.includes(role);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchTrainingCompetenceRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load training and competence");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const modulePolicy = (payload?.policy || []).find((row) => String(row?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options) ? modulePolicy.iso_clause_options.map(String) : ["7"];
      const fallback = String(modulePolicy?.iso_clause_default || options[0] || "7");
      setDefaultIsoClause(fallback);
      setRequestRoles((modulePolicy?.request_roles || []).map((x) => String(x).toLowerCase()));
      setApproveRoles((modulePolicy?.approve_roles || []).map((x) => String(x).toLowerCase()));
      setReviewRoles((modulePolicy?.review_roles || []).map((x) => String(x).toLowerCase()));
      setWorkflowMode(String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase());
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallback }));
    } catch {
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "7" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const summary = useMemo(() => {
    const total = items.length;
    const complete = items.filter((item) => COMPLETION_STATUSES.includes(String(item?.status || "").toLowerCase())).length;
    const overdue = items.filter((item) => String(item?.status || "").toLowerCase() === "overdue").length;
    return [
      { label: "Total records", value: total },
      { label: "Completed", value: complete },
      { label: "Open", value: total - complete },
      { label: "Overdue", value: overdue },
    ];
  }, [items]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      person_name: item.person_name || "",
      role_title: item.role_title || "",
      training_topic: item.training_topic || "",
      competence_area: item.competence_area || "",
      provider: item.provider || "",
      due_date: item.due_date || "",
      completed_date: item.completed_date || "",
      owner: item.owner || "",
      status: item.status || "planned",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.person_name || "").trim() || !String(form.role_title || "").trim() || !String(form.training_topic || "").trim()) {
      toast.error("Person, role title and training topic are required");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateTrainingCompetenceRegisterItemApi(editingId, form);
        toast.success("Training record updated");
      } else {
        await createTrainingCompetenceRegisterItemApi(form);
        toast.success("Training record created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save training record");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this training record?")) return;
    try {
      setDeletingId(id);
      await deleteTrainingCompetenceRegisterItemApi(id);
      toast.success("Training record deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete training record");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({ module_key: MODULE_KEY, item_id: item.id, notes });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({ module_key: MODULE_KEY, item_id: item.id, decision, notes });
      toast.success(`Training record ${decision}d`);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision} training record`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await downloadTrainingCompetenceRegisterCsvApi();
      downloadBlob(blob, "training_competence_register.csv");
    } catch (err) {
      toast.error(err?.message || "Failed to export training register");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 support</p>
          <h1>Training & Competence</h1>
          <p className="subtitle">Record training requirements, assign responsibility, and retain evidence that people are competent to carry out environmental responsibilities.</p>
        </div>
        <button className="btn-secondary" type="button" onClick={handleExport} disabled={exporting || loading}>
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="summary-row">
        {summary.map((item) => (
          <div className="kpi-card" key={item.label}>
            <div className="kpi-label">{item.label}</div>
            <h2>{loading ? "-" : item.value}</h2>
          </div>
        ))}
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to complete a record</p>
          <h3>Record the learning need and the evidence of competence</h3>
          <p>Create one record for each person and training topic. Keep the requirement, due date, completion evidence and any refresher requirements together.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Who and why</strong><span>Name the person, their role and the environmental responsibility or skill the training supports.</span></div>
          <div><strong>Plan and complete</strong><span>Record the provider and due date, then add the completion date only when training has taken place.</span></div>
          <div><strong>Keep evidence</strong><span>Attach certificates, attendance records, assessments or other proof of competence.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header"><div><p className="guided-register-eyebrow">Your training record</p><h3>{editingId ? "Edit training record" : "Create training record"}</h3></div><span className="kpi-sub">Use the record to show planned learning, completion and evidence of competence.</span></div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} />
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving || loading}>
                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <small>Use planned or booked before the activity; use completed only once evidence is available.</small>
            </label>
            <label className="settings-field">
              <span>Person name</span>
              <input placeholder="e.g. Jordan Smith" value={form.person_name} onChange={(e) => setField("person_name", e.target.value)} disabled={saving || loading} />
              <small>Name the employee, worker or contractor completing the training.</small>
            </label>
            <label className="settings-field">
              <span>Role title</span>
              <input placeholder="e.g. Warehouse Operative" value={form.role_title} onChange={(e) => setField("role_title", e.target.value)} disabled={saving || loading} />
              <small>Record the role so the competence requirement is traceable.</small>
            </label>
            <label className="settings-field">
              <span>Training topic</span>
              <input placeholder="e.g. Waste segregation and spill response" value={form.training_topic} onChange={(e) => setField("training_topic", e.target.value)} disabled={saving || loading} />
              <small>State the course, briefing, instruction or assessment required.</small>
            </label>
            <label className="settings-field">
              <span>Competence area</span>
              <input placeholder="e.g. Waste management" value={form.competence_area} onChange={(e) => setField("competence_area", e.target.value)} disabled={saving || loading} />
              <small>Name the environmental topic or responsibility covered.</small>
            </label>
            <label className="settings-field">
              <span>Provider</span>
              <input placeholder="e.g. Hindsight Sustainability" value={form.provider} onChange={(e) => setField("provider", e.target.value)} disabled={saving || loading} />
              <small>Record the training provider, internal trainer or source of instruction.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Operations Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving || loading} />
              <small>Name the person or role responsible for arranging and checking completion.</small>
            </label>
            <label className="settings-field">
              <span>Due date</span>
              <input type="date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} disabled={saving || loading} />
              <small>Set the latest acceptable completion date or refresher deadline.</small>
            </label>
            <label className="settings-field">
              <span>Completed date</span>
              <input type="date" value={form.completed_date} onChange={(e) => setField("completed_date", e.target.value)} disabled={saving || loading} />
              <small>Enter this only when the person has completed the requirement.</small>
            </label>
            <EvidenceReferenceField
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving || loading}
              helpText="Link a certificate, attendance record, assessment, briefing sign-off or other evidence of competence."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <textarea rows={3} placeholder="Optional: record refresher needs, assessment outcome, restrictions or follow-up actions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving || loading} />
              <small>Use this space for follow-up, competence observations or refresher requirements.</small>
            </label>
          </div>
          <div className="settings-actions">
            <button className="btn-primary" type="submit" disabled={saving || loading}>
              {saving ? "Saving..." : editingId ? "Update training record" : "Create training record"}
            </button>
            {editingId ? (
              <button className="btn-secondary" type="button" onClick={resetForm} disabled={saving}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Training topic</th>
              <th>Status</th>
              <th>Due date</th>
              <th>Completed</th>
              <th>Approval</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-empty">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="table-empty">No training records yet.</td></tr>
            ) : (
              items.map((item) => {
                const requestable = canRequestApprovalForRecord(item, canRequest, COMPLETION_STATUSES);
                return (
                  <tr key={item.id}>
                    <td>{item.person_name || "-"}</td>
                    <td>{item.training_topic || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>{item.due_date || "-"}</td>
                    <td>{item.completed_date || "-"}</td>
                    <td>{item.approval_status || "not_requested"}</td>
                    <td className="table-actions">
                      <button className="btn-small" type="button" onClick={() => startEdit(item)}>Edit</button>
                      <button className="btn-small danger" type="button" onClick={() => handleDelete(item.id)} disabled={deletingId === item.id}>
                        {deletingId === item.id ? "Deleting..." : "Delete"}
                      </button>
                      {requestable ? (
                        <button className="btn-small" type="button" onClick={() => handleRequestApproval(item)} disabled={approvalBusyId === item.id}>
                          Request approval
                        </button>
                      ) : null}
                      {workflowMode === "two_step" && canReview && isApprovalPendingReview(item) ? (
                        <button className="btn-small" type="button" onClick={() => handleDecision(item, "review")} disabled={approvalBusyId === item.id}>
                          Review
                        </button>
                      ) : null}
                      {canApprove && isApprovalPending(item) ? (
                        <>
                          <button className="btn-small" type="button" onClick={() => handleDecision(item, "approve")} disabled={approvalBusyId === item.id}>
                            Approve
                          </button>
                          <button className="btn-small danger" type="button" onClick={() => handleDecision(item, "reject")} disabled={approvalBusyId === item.id}>
                            Reject
                          </button>
                        </>
                      ) : null}
                      {isApprovalApproved(item) ? <span className="kpi-sub">Approved</span> : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
